import React from "react";
import { Link } from "react-router-dom";
import { Star, ShoppingCart } from "lucide-react";
import { Product } from "../types";
import { formatCurrency } from "../utils/currency";
import ShareButton from "./ShareButton";

interface ProductCardProps {
  product: Product;
  onAddToCart?: (product: Product) => void;
  showShareButton?: boolean;
}

export const ProductCard: React.FC<ProductCardProps> = ({
  product,
  onAddToCart,
  showShareButton = true,
}) => {
  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
      );
    }

    if (hasHalfStar) {
      stars.push(
        <Star key="half" className="h-4 w-4 fill-yellow-400 text-yellow-400" />
      );
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<Star key={`empty-${i}`} className="h-4 w-4 text-gray-300" />);
    }

    return stars;
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover-scale-playful overflow-hidden group border-2 border-transparent hover:border-purple-300">
      <Link to={`/product/${product.id}`}>
        {/* Product Image */}
        <div className="relative overflow-hidden">
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-300"
          />

          {/* Sale Badge - More playful */}
          {product.onSale && (
            <div className="absolute top-3 left-3 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs font-extrabold px-3 py-1.5 rounded-full shadow-lg animate-bounce-gentle border-2 border-white">
              🔥 SALE! 🔥
            </div>
          )}

          {/* Quick View Overlay - More playful */}
          <div className="absolute inset-0 bg-gradient-to-t from-purple-600/40 to-transparent bg-opacity-0 group-hover:bg-opacity-100 transition-all duration-300 flex items-center justify-center">
            <span className="opacity-0 group-hover:opacity-100 transition-all duration-300 bg-gradient-to-r from-white to-purple-50 text-purple-700 px-6 py-3 rounded-full font-bold text-sm shadow-2xl transform group-hover:scale-110 border-2 border-white">
              👀 View Details 👀
            </span>
          </div>
        </div>
      </Link>

      {/* Product Info */}
      <div className="p-4">
        <Link to={`/product/${product.id}`}>
          {/* Product Name */}
          <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-soft-purple-600 transition-colors duration-200">
            {product.name}
          </h3>
        </Link>

        {/* Rating */}
        {product.rating > 0 || product.reviewCount > 0 ? (
          <div className="flex items-center mb-2">
            <div className="flex items-center mr-2">
              {renderStars(product.rating)}
            </div>
            <span className="text-sm text-gray-600">
              {product.rating.toFixed(1)}/5 ({product.reviewCount})
            </span>
          </div>
        ) : (
          <div className="mb-2">
            <span className="text-sm text-gray-400 italic">No ratings yet</span>
          </div>
        )}

        {/* Age Range */}
        <p className="text-sm text-gray-500 mb-3">
          {product.ageRange ? `Ages: ${product.ageRange}` : "All ages"}
        </p>

        {/* Price */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            {product.price > 0 ? (
              <>
                <span className="text-lg font-bold text-gray-900">
                  {formatCurrency(product.price)}
                </span>
                {product.originalPrice &&
                  product.originalPrice > product.price && (
                    <span className="text-sm text-gray-500 line-through">
                      {formatCurrency(product.originalPrice)}
                    </span>
                  )}
              </>
            ) : (
              <span className="text-lg font-semibold text-purple-600">
                Ask for price
              </span>
            )}
          </div>

          {/* Stock Status */}
          <div
            className={`text-xs px-2 py-1 rounded-full ${
              product.inStock
                ? "bg-green-100 text-green-800"
                : "bg-red-100 text-red-800"
            }`}
          >
            {product.inStock ? "In Stock" : "Out of Stock"}
          </div>
        </div>

        {/* Share/Order Button - More playful */}
        {showShareButton ? (
          <ShareButton
            product={{
              productName: product.name,
              price: product.price,
              productUrl: `${window.location.origin}/product/${product.id}`,
            }}
            fullProduct={product}
          />
        ) : (
          <button
            onClick={() => onAddToCart?.(product)}
            disabled={!product.inStock}
            className={`w-full flex items-center justify-center py-3 px-4 rounded-xl font-bold text-sm transition-all duration-300 ${
              product.inStock
                ? "bg-gradient-to-r from-purple-500 via-pink-500 to-purple-600 hover:from-purple-600 hover:via-pink-600 hover:to-purple-700 text-white shadow-xl hover:shadow-2xl transform hover:scale-110 hover-bounce border-2 border-white/50"
                : "bg-gray-300 text-gray-500 cursor-not-allowed rounded-xl"
            }`}
          >
            <ShoppingCart className="h-5 w-5 mr-2" />
            {product.inStock ? "🛒 Add to Cart" : "❌ Out of Stock"}
          </button>
        )}
      </div>
    </div>
  );
};

export default ProductCard;
